import { FormControl, InputLabel, MenuItem, Select } from '@mui/material'
import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux';
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { eachDeep } from "deepdash-es/standalone";
import { cloneDeep } from 'lodash';
import { treeActions } from '../../../redux/slices/treeSlice';

function VariableCollection({updatedSelectedNode, modifyUpdatedNode, hasUpdatedNode}) {
  const { t } = useTranslation();
  const parentScreen = useContext(ParentScreenCtx)
  const dispatch = useDispatch()
  const { rightTree } = useSelector(state => state.treeData[parentScreen]);
  const {translatedData,selectedNode} = rightTree.tree[rightTree.currentTab];
  const [VCValue,setVCValue] = useState('VARIABLE_COLLECTION')
  const [variableCollectionOptions, setVariableCollectionOptions] = useState([t('SELECT')])

  
  const extractVariableCollectionNames = useCallback((data) => {
    const names = [];
    const { FILETYPE } = rightTree.tree.templateHeader;
    
    // Helper function to check if all parent records are non-looping (single occurrence)
    const areAllParentRecordsNonLooping = (obj, treeData) => {
      // Find the parent record by traversing up the hierarchy
      let current = findParent(treeData, obj);
      
      while (current) {
        // If current object is a loop record, check if it's single (non-looping)
        if (current.LOOP === "MULTI") {
          // Special exception for DATABASE file types 
          if (FILETYPE === "DATABASE") {
            return true;
          }
          return false; // Parent is looping, exclude this variable
        }
        
        // Move up to parent - we need to find parent in the tree structure
        current = findParent(treeData, current);
      }
      return true; // All parents are non-looping or no looping parents found
    };
    
    // Helper function to find parent object in the tree
    const findParent = (treeData, targetObj) => {
      for (let item of treeData) {
        if (item.children && item.children.includes(targetObj)) {
          return item;
        }
        if (item.children) {
          const foundParent = findParent(item.children, targetObj);
          if (foundParent) return foundParent;
        }
      }
      return null;
    };
    
    // Get current section context (header, body, trailer)
    const getCurrentSection = () => {
      const currentTab = rightTree.currentTab;
      if (currentTab === 'header') return 'header';
      if (currentTab === 'trailer') return 'trailer';
      return 'body';
    };
    
    const extractNames = (obj, isInHeader = false, treeData = data) => {
      if (obj.TYPE === "VARIABLE_COLLECTION" && obj.NAME) {
        names.push(obj.NAME);
      }
  
      if (obj.children && obj.children.length > 0) {
        obj.children.forEach(child => extractNames(child, isInHeader, treeData));
      }
    };
    
    // Special function for header variables that applies parent checking ONLY to direct children
    // children get parent checking, nested children don't
    const extractHeaderNames = (obj, treeData, isDirectChild = true) => {
      if (obj.TYPE === "VARIABLE_COLLECTION" && obj.NAME) {
        if (isDirectChild) {
          // Direct child: Apply parent checking 
          const shouldInclude = areAllParentRecordsNonLooping(obj, treeData);
          if (shouldInclude) {
            names.push(obj.NAME);
          }
        } else {
          names.push(obj.NAME);
        }
      }
  
      if (obj.children && obj.children.length > 0) {
        // Nested children are processed without parent checking (useHeaderRule = false)
        obj.children.forEach(child => extractHeaderNames(child, treeData, false));
      }
    };
    
    // Process different sections based on current context 
    const currentSection = getCurrentSection();
    
    // Always process header variables (for all sections)
    const headerData = rightTree.tree.header?.translatedData || [];
    headerData.forEach(item => extractHeaderNames(item, headerData, true));
    
    // Always process body variables (for all sections) - "everyone gets the body"
    const bodyData = rightTree.tree.body?.translatedData || [];
    bodyData.forEach(item => extractNames(item, false, bodyData));
    
    // Only process trailer variables when we're in trailer section 
    if (currentSection === 'trailer') {
      const trailerData = rightTree.tree.trailer?.translatedData || [];
      trailerData.forEach(item => extractNames(item, false, trailerData));
    }
  
    return [...new Set(names)].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })); // Remove duplicates and sort
  }, [rightTree]);

  const handleVariableCollectionChange = (key,value) => {
    setVCValue(value);
    if (hasUpdatedNode) modifyUpdatedNode(key, value)
    else {
      const updatedData = eachDeep(cloneDeep(translatedData), (obj) => {
        if (obj?.key !== selectedNode.key) {
          return;
        }
        obj[key] = value;
      });
      dispatch(
        treeActions.setNewRightTreeTranslatedData({
          screen: parentScreen,
          type: rightTree.currentTab,
          data: updatedData,
        })
      );
    }
  };


  useEffect(() => {
    setVariableCollectionOptions([t('SELECT'), ...extractVariableCollectionNames(translatedData)]);
  }, [translatedData, extractVariableCollectionNames, t]);

  useEffect(() => {
    if (selectedNode) {
      const vcDefault = hasUpdatedNode ? (updatedSelectedNode?.LOOP_SOURCE_NAME  || 'VARIABLE_COLLECTION') : (selectedNode?.LOOP_SOURCE_NAME || 'VARIABLE_COLLECTION')
      setVCValue(vcDefault || 'VARIABLE_COLLECTION');
    }
  }, [selectedNode?.key]);


  const varialbleCollectionMenuItems = useMemo(
    () =>
      variableCollectionOptions.map((obj, key) => (
        <MenuItem
          key={key}
          value={obj === t('SELECT') ? "VARIABLE_COLLECTION" : obj}
          >
          {obj}
        </MenuItem>
      )),
    [variableCollectionOptions]
  );

  return (
    <>
     <FormControl variant='outlined' className='width-100' size='small'>
      <InputLabel> {t('LBL_VAR_COLL')} </InputLabel>
      <Select
       className='width-100'
       MenuProps={{
        PaperProps: {
          sx: {
            maxHeight: 300,
          },
        },
      }}
      name='variableCollection'
      value={VCValue}
      label={t('LBL_VAR_COLL')}
      onChange={(e) =>{
        handleVariableCollectionChange('LOOP_SOURCE_NAME',e.target.value)
      }}
      >
      {varialbleCollectionMenuItems}
      </Select>
     </FormControl>
    </>
  )
}

export default VariableCollection